/* eslint-disable ember/no-controller-access-in-routes, no-unused-vars, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import ReportTemplateSectionType from '@linc-technologies/linced-common/enums/reports/report-template-section-type';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import { loadTemplateWithSections } from 'linced-app/utils/reporting';

export default class ReviewRoute extends Route {
  templateName = 'reporting/review';

  // Services
  @service
  session;

  @service
  store;

  @service
  router;

  async model(params) {
    let { person } = this.modelFor('reporting.group.student');

    let studentReport;
    try {
      studentReport =
        this.store.peekRecord(
          'reports/student-report',
          params.student_report_id,
        ) ||
        (await this.store.findRecord(
          'reports/student-report',
          params.student_report_id,
        ));
    } catch (err) {
      this.router.transitionTo('reporting.group.student');
      return;
    }

    let templateId = studentReport.belongsTo('reportTemplateId').id();
    let template;
    try {
      template = await loadTemplateWithSections(this.store, templateId);
    } catch (err) {
      this.router.transitionTo('reporting.group.student');
      return;
    }

    // Load student-report-sections
    if (studentReport) {
      await this.store.query('reports/student-report-section', {
        studentReportId: studentReport.get('id'),
      });
    }

    // Preload subjects, levels, and expectations for every content section
    let contentSections = this.store
      .peekAll('reports/report-template-section')
      .filter(
        (section) =>
          section.belongsTo('reportTemplateId').id() === template.get('id') &&
          section.sectionType === ReportTemplateSectionType.CONTENT,
      );

    let subjectIds = new Set();
    let progressVisualSubjectIds = new Set();
    contentSections.forEach((section) => {
      let config = section.get('configuration');
      if (!config) return;
      try {
        let parsed = typeof config === 'string' ? JSON.parse(config) : config;
        if (!parsed.subjectId) return;
        subjectIds.add(parsed.subjectId);
        let hasProgressVisual = (parsed.components || []).some(
          (c) => c.type === 'progress-visual',
        );
        if (hasProgressVisual) {
          progressVisualSubjectIds.add(parsed.subjectId);
        }
      } catch (err) {
        // ignore
      }
    });

    // Levels/expectations are needed for every subject regardless of
    // component type — resolveLevelId uses them to save judgement/effort
    // progressions for strand-visual and progress-descriptor sliders too, not
    // just progress-visual charts. Progress-visual subjects additionally need
    // the progression records themselves for the chart line.
    if (subjectIds.size) {
      await Promise.all(
        Array.from(subjectIds).map((subjectId) =>
          Promise.all([
            this.store.findRecord('goals/subject', subjectId).catch(() => null),
            this.store
              .query('milestones/level', { goalSubjectId: subjectId })
              .catch(() => null),
            this.store
              .query('milestones/expectation', { goalSubjectId: subjectId })
              .catch(() => null),
            ...(progressVisualSubjectIds.has(subjectId)
              ? [
                  this.store
                    .query('milestones/progression', {
                      goalSubjectId: subjectId,
                      type: ProgressionType.PROGRESSION,
                      latest: true,
                    })
                    .catch(() => null),
                ]
              : []),
          ]),
        ),
      );
    }

    return { template, person, studentReport };
  }

  setupController(controller, model) {
    super.setupController(...arguments);
    let groupCtrl = this.controllerFor('reporting.group');
    groupCtrl.set('isReviewRoute', true);
    groupCtrl.set('activeReport', model.template);

    let studentCtrl = this.controllerFor('reporting.group.student');
    studentCtrl.set('isStudentReviewRoute', true);

    let reportMap = {};
    if (model.studentReport) {
      reportMap[model.person.get('id')] = model.studentReport;
    }

    controller.set('person', model.person);
    controller.set('template', model.template);
    controller.set('reviewStudentReports', reportMap);
    controller.set('reviewSelectedStudent', model.person);
  }

  resetController(controller, isExiting) {
    if (isExiting) {
      this.controllerFor('reporting.group').set('isReviewRoute', false);
      this.controllerFor('reporting.group').set('activeReport', null);
      this.controllerFor('reporting.group.student').set(
        'isStudentReviewRoute',
        false,
      );
      controller.cleanupReviewView();
    }
  }
}
